import { useState, useEffect, useImperativeHandle, forwardRef, type RefObject } from 'react';
import { Plus } from 'lucide-react';
import { AddItemSheet } from './AddItemSheet';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useScrollOffset } from '@/hooks/useScrollOffset';
import { triggerHaptic } from '@/hooks/useHaptics';

interface AddItemFABProps {
  initialUrl?: string | null;
  onInitialUrlProcessed?: () => void;
  showAttentionAnimation?: boolean;
  /** When set, newly saved items will be automatically added to this collection */
  autoAddToCollectionId?: string;
  /** Collection name for toast message */
  autoAddToCollectionName?: string;
  /** Ref to the scrollable container to track scroll offset */
  scrollRef?: RefObject<HTMLElement | null>;
  /** Hide the FAB with scale animation (e.g. during multi-select) */
  hidden?: boolean;
}

export interface AddItemFABRef {
  openSheet: () => void;
}

export const AddItemFAB = forwardRef<AddItemFABRef, AddItemFABProps>(function AddItemFAB(
  { initialUrl, onInitialUrlProcessed, showAttentionAnimation = false, autoAddToCollectionId, autoAddToCollectionName, scrollRef, hidden },
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const fallbackRef = { current: null } as RefObject<HTMLElement | null>;
  const isScrolled = useScrollOffset(scrollRef || fallbackRef, 20);

  // Expose the openSheet method to parent components
  useImperativeHandle(ref, () => ({
    openSheet: () => setIsOpen(true),
  }));

  // Auto-open when an initial URL is provided (from bookmarklet)
  useEffect(() => {
    if (initialUrl) {
      setIsOpen(true);
    }
  }, [initialUrl]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && initialUrl) {
      onInitialUrlProcessed?.();
    }
  };

  return (
    <>
      <div 
        className={cn(
          "fixed right-4 flex flex-col items-center gap-3 z-40 transition-all duration-300 origin-center",
          (isScrolled || hidden) && "scale-0 opacity-0"
        )}
        style={{ bottom: Capacitor.isNativePlatform()
          ? 'calc(5.25rem + env(safe-area-inset-bottom))'
          : '6.25rem'
        }}
      >
        <button
          onClick={() => {
            triggerHaptic('medium');
            setIsOpen(true);
          }}
          className="w-14 h-14 flex items-center justify-center active:scale-95 transition-transform rounded-full shadow-lg border border-primary/20 bg-brand-gradient"
          aria-label="Add item"
        >
          <Plus 
            className={cn(
              "w-7 h-7 text-primary-foreground transition-transform duration-300",
              isOpen && "rotate-45",
              showAttentionAnimation && !isOpen && "animate-pulse"
            )} 
            strokeWidth={2.5}
          />
        </button>
      </div>

      <AddItemSheet 
        open={isOpen} 
        onOpenChange={handleOpenChange} 
        initialUrl={initialUrl || undefined}
        autoAddToCollectionId={autoAddToCollectionId}
        autoAddToCollectionName={autoAddToCollectionName}
      />
    </>
  );
});
